import type { WorldCupPayload } from '@/api/worldCup';
import type { TabKey } from './components/useTabParam';

/*
 * Per-report dashboard config type. Add a new report by creating a file in
 * configs/ and appending it to configs/index.ts — App.tsx auto-generates a
 * route from it.
 */

// Re-export from clusterBase.ts (separate file to avoid circular imports with configs/).
export { clusterBase, AUTH_BASE_URL } from './clusterBase';

export type DashboardConfig = {
  /** UI route, e.g. `/carrefour/brazil`. */
  path: string;
  /** Backend report id: GET <apiBaseUrl>/reports/<reportId>. */
  reportId: string;
  /** Human label shown in the report switcher. */
  label: string;
  /** Which dashboard to render. 'worldcup' (default), 'ah', 'prijsfavorieten', 'monopoly', 'survey', 'minions' or 'morrisons'. */
  kind?: 'worldcup' | 'ah' | 'prijsfavorieten' | 'monopoly' | 'survey' | 'minions' | 'morrisons';
  /** Resolved base URL for this report's DATA backend. */
  apiBaseUrl: string;
  /** Section / column visibility. Defaults to "show everything". */
  hide?: {
    revenue?: boolean;
    avgBasket?: boolean;
    usersTraffic?: boolean;
    itemsPerTx?: boolean;
    gamesByBrand?: boolean;
    topPrizes?: boolean;
    /** Drop the "Games played" KPI — campaigns with no game mechanic at all. */
    gamesPlayed?: boolean;
    /** Drop the whole "Games & Missions" tab (same case as gamesPlayed). */
    gamesTab?: boolean;
  };
  /** Static payload fallback when the backend pipeline isn't live yet. */
  staticPayload?: WorldCupPayload;
  /**
   * Design-handoff payload rendered in DEV ONLY, so a dashboard can be reviewed
   * against its design before the pipeline exists. Deliberately NOT used in a
   * deployed build — a client must never be shown handoff figures as if they
   * were live (unlike `staticPayload`, which is shown everywhere).
   */
  devPreviewPayload?: WorldCupPayload;
  /** Where the per-brand performance table lives. Omit to suppress it. */
  segmentsLocation?: 'overview' | 'games';
  /** Show "Export to Excel" button. */
  hasExport?: boolean;
  /**
   * Per-report tab label overrides. The shared labels assume every campaign has
   * the same mechanics; Coca-Cola Quick has rewards but no coupons, so its
   * "Rewards & Coupons" tab is just "Rewards" (as the design has it).
   */
  tabLabels?: Partial<Record<TabKey, string>>;
  /**
   * The campaign's last day (YYYY-MM-DD). From the day after, the report is
   * ARCHIVED: the report switcher lists it under the running campaigns, and it
   * is never the page a user lands on while a running one is available. A date
   * rather than a flag, so a campaign moves down on its own when it ends.
   */
  endsOn: string;
  /**
   * The pipeline is retired in code and the payload is frozen: admin → Report
   * runs never offers "Run now" for it, so the frozen figures cannot be
   * overwritten. Not the same as ended — an ended campaign can still be re-run.
   */
  frozen?: boolean;
};

// --- Helpers (imported by App.tsx, layouts, pages) ---

import { DASHBOARD_CONFIGS } from './configs';
export { DASHBOARD_CONFIGS };

export const KNOWN_REPORTS: ReadonlyArray<{ id: string; label: string }> = DASHBOARD_CONFIGS.map((c) => ({
  id: c.reportId,
  label: c.label,
}));

/** Reverse lookup: which UI path serves a given report id? */
export function pathForReport(reportId: string): string | null {
  return DASHBOARD_CONFIGS.find((c) => c.reportId === reportId)?.path ?? null;
}

/** The viewer's calendar day, YYYY-MM-DD — what "has this campaign ended?" is asked against. */
export const localToday = (): string => new Date().toLocaleDateString('en-CA');

/** True once the campaign's last day is behind us. */
export const isArchived = (c: Pick<DashboardConfig, 'endsOn'>, today = localToday()): boolean => c.endsOn < today;

/**
 * The switcher's order: running campaigns first, then archived ones; inside
 * each group the latest-ending campaign first, so the newest sits on top.
 */
export function activeFirst(configs: ReadonlyArray<DashboardConfig>, today = localToday()): DashboardConfig[] {
  const newestFirst = (a: DashboardConfig, b: DashboardConfig) => b.endsOn.localeCompare(a.endsOn);
  return [...configs.filter((c) => !isArchived(c, today)).sort(newestFirst), ...configs.filter((c) => isArchived(c, today)).sort(newestFirst)];
}

/** Fallback landing report: the first in the switcher's order over every config. */
export function defaultPath(): string {
  return activeFirst(DASHBOARD_CONFIGS)[0].path;
}

/** First report from the JWT's `allowed_reports` that we have a config for, in the switcher's order. */
export function firstAllowedPath(allowedReports: ReadonlyArray<string>): string | null {
  const allowed = allowedReports.map((r) => DASHBOARD_CONFIGS.find((c) => c.reportId === r)).filter((c): c is DashboardConfig => c != null);
  return activeFirst(allowed)[0]?.path ?? null;
}

/**
 * Where a signed-in user lands when the URL names no report: the first entry
 * of their report switcher (admins: every report; clients: their grants).
 */
export function landingPath(allowedReports: ReadonlyArray<string>, isAdmin: boolean, today = localToday()): string {
  return switchableConfigs(allowedReports, isAdmin, today)[0]?.path ?? defaultPath();
}

// Report ids already reported as unconfigured, so the warning below fires once
// per id rather than on every render.
const warnedMissingConfigs = new Set<string>();

/**
 * Configs the user is allowed to see, in the token's order.
 *
 * A granted report with no config here CANNOT be rendered (no route, no cluster
 * URL, no dashboard kind), so it is dropped — but silently dropping it is how a
 * deploy-drift bug hides: the backend grants a report the UI build predates, and
 * the user simply never sees it. In dev the gap is named once per id.
 */
export function allowedConfigs(allowedReports: ReadonlyArray<string>): DashboardConfig[] {
  const configs = allowedReports.map((r) => DASHBOARD_CONFIGS.find((c) => c.reportId === r)).filter((c): c is DashboardConfig => c != null);

  if (import.meta.env.DEV && configs.length !== allowedReports.length) {
    const configured = new Set(DASHBOARD_CONFIGS.map((c) => c.reportId));
    for (const id of allowedReports) {
      if (!configured.has(id) && !warnedMissingConfigs.has(id)) {
        warnedMissingConfigs.add(id);
        console.warn(`[dashboard] the token grants "${id}" but this build has no config for it — it cannot appear in the report switcher.`);
      }
    }
  }
  return configs;
}

/** Configs for the report switcher. Admins see all; clients see their grants.
 *  Active campaigns list first, archived ones sink to the bottom. */
export function switchableConfigs(allowedReports: ReadonlyArray<string>, isAdmin: boolean, today = localToday()): DashboardConfig[] {
  return activeFirst(isAdmin ? DASHBOARD_CONFIGS : allowedConfigs(allowedReports), today);
}

/** One entry of a dashboard header's report switcher. */
export type SwitchOption = { reportId: string; path: string; label: string; archived: boolean };

const toOption = (c: DashboardConfig, today: string): SwitchOption => ({ reportId: c.reportId, path: c.path, label: c.label, archived: isArchived(c, today) });

/** The switcher's entries minus the report on screen, each marked active or archived. */
export function switchOptions(allowedReports: ReadonlyArray<string>, isAdmin: boolean, currentReportId: string, today = localToday()): SwitchOption[] {
  return switchableConfigs(allowedReports, isAdmin, today)
    .filter((c) => c.reportId !== currentReportId)
    .map((c) => toOption(c, today));
}

/** Every switcher entry, the report on screen included (the generic header selects it). */
export function allSwitchOptions(allowedReports: ReadonlyArray<string>, isAdmin: boolean, today = localToday()): SwitchOption[] {
  return switchableConfigs(allowedReports, isAdmin, today).map((c) => toOption(c, today));
}

/** Find the config matching a given UI path. */
export function configForPath(path: string): DashboardConfig | null {
  return DASHBOARD_CONFIGS.find((c) => c.path === path) ?? null;
}
